import * as React from "react";
import type { TFunction } from "i18next";
import Dropzone, { type DropzoneProps, type FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ScrollArea } from "@/components/_atoms/scroll-area";
import { FileCard } from "@/components/_organisms/csv-import-dialog/file-card";
import { isFileWithPreview } from "@/components/_organisms/csv-import-dialog/is-file-with-preview";
import { useControllableState } from "@/hooks/use-controllable-state";
import { cn } from "@/lib/cn";
import { formatBytes } from "@/lib/format-bytes";
import { UploadIcon } from "@radix-ui/react-icons";

const DEFAULT_MAX_SIZE_IN_BYTES = 1024 * 1024 * 2;
const DEFAULT_MAX_FILE_COUNT = 1;

interface FileUploaderProps extends React.HTMLAttributes<HTMLDivElement> {
  value?: File[];
  onValueChange?: (files: File[]) => void;
  onUpload?: (files: File[]) => Promise<void>;
  progresses?: Record<string, number>;
  accept?: DropzoneProps["accept"];
  maxSize?: DropzoneProps["maxSize"];
  maxFileCount?: DropzoneProps["maxFiles"];
  multiple?: boolean;
  disabled?: boolean;
}

interface DropValidationParams {
  acceptedFileCount: number;
  currentFileCount: number;
  maxFileCount: number;
  multiple: boolean;
  t: TFunction<"common">;
}

function getDropRejectionMessage({
  acceptedFileCount,
  currentFileCount,
  maxFileCount,
  multiple,
  t,
}: DropValidationParams): string | null {
  if (!multiple && maxFileCount === 1 && acceptedFileCount > 1) {
    return t("fileUpload.maxOneFile");
  }

  if (currentFileCount + acceptedFileCount > maxFileCount) {
    return t("fileUpload.maxFiles", { maxFileCount });
  }

  return null;
}

function revokePreviewList(fileList: readonly File[]): void {
  fileList.forEach((file) => {
    if (isFileWithPreview(file)) {
      URL.revokeObjectURL(file.preview);
    }
  });
}

export function FileUploader(props: FileUploaderProps) {
  const { t } = useTranslation("common");
  const {
    value: valueProp,
    onValueChange,
    onUpload,
    progresses,
    accept = {
      "image/*": [],
    },
    maxSize = DEFAULT_MAX_SIZE_IN_BYTES,
    maxFileCount = DEFAULT_MAX_FILE_COUNT,
    multiple = false,
    disabled = false,
    className,
    ...dropzoneProps
  } = props;

  const [files, setFiles] = useControllableState({
    prop: valueProp,
    onChange: onValueChange,
  });

  const previewFileListRef = React.useRef<readonly File[]>([]);

  React.useEffect(() => {
    previewFileListRef.current = files ?? [];
  }, [files]);

  React.useEffect(() => {
    return () => revokePreviewList(previewFileListRef.current);
  }, []);

  const startUpload = React.useCallback(
    (fileList: File[]) => {
      if (!onUpload) {
        return;
      }

      const target =
        fileList.length > 1
          ? t("fileUpload.filesCount_other", { count: fileList.length })
          : t("fileUpload.filesCount_one");

      toast(t("fileUpload.uploading", { target }));

      onUpload(fileList)
        .then(() => {
          revokePreviewList(fileList);
          setFiles([]);
          toast(t("fileUpload.uploaded", { target }));
        })
        .catch(() => {
          toast(t("fileUpload.uploadFailed", { target }));
        });
    },
    [onUpload, setFiles, t],
  );

  const handleDrop = React.useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const rejectionMessage = getDropRejectionMessage({
        acceptedFileCount: acceptedFiles.length,
        currentFileCount: files?.length ?? 0,
        maxFileCount,
        multiple,
        t,
      });

      if (rejectionMessage) {
        toast(rejectionMessage);
        return;
      }

      const newFiles = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );

      const updatedFiles = files ? [...files, ...newFiles] : newFiles;

      setFiles(updatedFiles);

      rejectedFiles.forEach(({ file }) => {
        toast(t("fileUpload.fileRejected"), {
          description: t("fileUpload.fileRejectedDescription", { fileName: file.name }),
        });
      });

      if (updatedFiles.length > 0 && updatedFiles.length <= maxFileCount) {
        startUpload(updatedFiles);
      }
    },
    [files, maxFileCount, multiple, setFiles, startUpload, t],
  );

  function handleRemove(index: number) {
    if (!files) {
      return;
    }

    const removedFile = files[index];
    if (removedFile && isFileWithPreview(removedFile)) {
      URL.revokeObjectURL(removedFile.preview);
    }

    const newFiles = files.filter((_, fileIndex) => fileIndex !== index);
    setFiles(newFiles);
    onValueChange?.(newFiles);
  }

  const isDisabled = disabled || (files?.length ?? 0) >= maxFileCount;

  return (
    <div className="relative flex flex-col gap-6 overflow-hidden">
      <Dropzone
        onDrop={handleDrop}
        accept={accept}
        maxSize={maxSize}
        maxFiles={maxFileCount}
        multiple={maxFileCount > 1 || multiple}
        disabled={isDisabled}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            {...getRootProps()}
            className={cn(
              "group relative grid h-52 w-full cursor-pointer place-items-center rounded-md border-2 border-dashed border-muted-foreground/25 px-5 py-2.5 text-center transition hover:bg-muted/25",
              "ring-offset-background focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus",
              isDragActive && "border-muted-foreground/50",
              isDisabled && "pointer-events-none opacity-60",
              className,
            )}
            {...dropzoneProps}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                <div className="rounded-full border border-dashed p-3">
                  <UploadIcon className="size-7 text-muted-foreground" aria-hidden="true" />
                </div>
                <p className="font-medium text-muted-foreground">{t("fileUpload.dropHere")}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-4 sm:px-5">
                <div className="rounded-full border border-dashed p-3">
                  <UploadIcon className="size-7 text-muted-foreground" aria-hidden="true" />
                </div>
                <div className="flex flex-col gap-px">
                  <p className="font-medium text-muted-foreground">{t("fileUpload.dragOrClick")}</p>
                  <p className="text-sm text-muted-foreground/70">
                    {maxFileCount > 1
                      ? maxFileCount === Infinity
                        ? t("fileUpload.uploadMultipleUnlimited", { maxSize: formatBytes(maxSize) })
                        : t("fileUpload.uploadMultiple", { maxFileCount, maxSize: formatBytes(maxSize) })
                      : t("fileUpload.uploadSingle", { maxSize: formatBytes(maxSize) })}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </Dropzone>
      {files?.length ? (
        <ScrollArea className="h-fit w-full px-3">
          <div className="flex max-h-48 flex-col gap-4">
            {files.map((file, index) => (
              <FileCard
                key={index}
                file={file}
                onRemove={() => handleRemove(index)}
                progress={progresses?.[file.name]}
                disabled={disabled}
              />
            ))}
          </div>
        </ScrollArea>
      ) : null}
    </div>
  );
}
